import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'

import { EditInBulkPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import createComputed from 'modules/core/hooks/createComputed'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import type { RouteContextParams } from 'modules/employees/lib/RouteContextParams'
import EditInBulk from 'modules/employees/pages/EditInBulk'
import employees from 'modules/employees/store/collections/employees'
import type { Employees } from 'modules/employees/store/collections/employees'
import NuvoImporter from 'modules/universal_importer/components/NuvoImporter'

type LocationStateType = { employeesIds?: number[] } | null

const useEmployees = createComputed((_employees: Employees, selectedIds: Set<number>) => {
  if (!selectedIds.size) return _employees.toArray()

  return _employees.toArray().filter((employee) => selectedIds.has(employee.attributes.id))
})

const EditInBulkPage = () => {
  const { filtersForm } = useOutletContext<RouteContextParams>()
  const {
    company,
    access: { locale },
  } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const location = useLocation()
  const locationState: LocationStateType = location.state
  const selectedIds = new Set(locationState?.employeesIds ?? [])
  const { data, loading } = useQuery(EditInBulkPageDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const universalEditInBulk = data?.features.featuresConnection.nodes.find(
    (feature) => feature.name === 'dev_universal_employees_edition'
  )?.enabled

  const selectedEmployees = useEmployees(employees, selectedIds).get()

  const canViewPage = selectedEmployees.length
  if (!canViewPage) {
    navigate(navigation.employees.fullPath())
  }

  if (loading) {
    return <Loading />
  }

  if (universalEditInBulk) {
    return (
      <NuvoImporter
        importType="employee_edition"
        companyId={company.id}
        locale={locale}
        metadata={{
          employee_ids: Array.from(selectedIds),
        }}
        isEdition
      />
    )
  }

  return <EditInBulk filtersForm={filtersForm} employees={selectedEmployees} />
}

export default EditInBulkPage
